// Phase S «Mượt» (đợt 261030) — logic thuần cho: banner «Máy đang tự thử lại» (chain.autoRetry), đạo cụ dò thiếu ADVISORY,
// «Khôi phục» đạo cụ máy đã tự gộp, «Bỏ qua tất cả». Hợp đồng DTO: `_workers/reports/s-fe-contract.md`.
// KHÔNG import giá trị từ workflowFourUtils (workflowFourUtils import file này) — chỉ import type.
import { PLAN_BUSY_TEXT } from './planBusy';
import type { WorkflowFourFinding, WorkflowFourStatus } from './workflowFourUtils';

/** BE `chain.autoRetry` (Quy tắc A). `exhausted` ⇒ đã đủ 3 vòng, chờ người. */
export interface WorkflowFourAutoRetry { segment: number; round: number; at: string; delayS?: number; codes?: string[]; exhausted?: boolean }

/** Quá hạn hẹn giờ bấy nhiêu mà chuỗi vẫn FAILED ⇒ hẹn giờ đã mất (BE khởi động lại): xử như lỗi thường, không treo «sắp thử lại». */
export const AUTO_RETRY_GRACE_MS = 15_000;

export type AutoRetryView =
  | { kind: 'scheduled'; round: number; secondsLeft: number; title: string; description: string }
  | { kind: 'running'; round: number; secondsLeft: 0; title: string; description: string }
  | { kind: 'exhausted'; round: number; secondsLeft: 0; title: string; description: string };

/** Lý do ngắn (tiếng Việt) cho các mã trong danh sách trắng Quy tắc A. */
export function autoRetryReasonOf(codes: string[] | undefined): string | null {
  const text = Array.from(new Set((codes ?? []).map((code): string | null => {
    if (code === 'HTTP_429') return 'hạn mức AI tạm hết';
    if (code === 'PROVIDER_5XX' || /^HTTP_5\d\d$/.test(code)) return 'máy chủ AI gặp sự cố';
    if (code === 'P2028' || code === 'TRANSACTION_EXPIRED') return 'cơ sở dữ liệu bận';
    if (code === 'PROVIDER_BODY_ABORTED') return 'kết nối nhận ảnh bị đứt giữa chừng';
    if (code === 'LEDGER') return 'hai luồng ghi trùng sổ chi phí (máy tự đối soát)';
    // 261030 S12g (G1/G2/G4): lỗi hạ tầng của chuỗi + «chưa rõ» non-video + tấm bị lọc nội dung máy tự bỏ qua — máy tự xử, không cần bấm.
    if (code === 'OWNER_LOST') return 'tiến trình đang chạy bị dừng giữa chừng (thường do máy chủ khởi động lại)';
    if (code === 'PLAN_CREATE_FAILED') return 'việc tạo plan dừng giữa chừng';
    if (code === 'LAUNCH_FAILED') return 'chưa mở được phần kế tiếp';
    if (code === 'UNCERTAIN' || code === 'ASSUMED_LOST') return 'một lượt vẽ/viết chưa rõ kết quả — máy tự đối soát';
    if (code === 'CANDIDATE_SAVE_TRANSIENT' || code === 'STRICT_READ_UNKNOWN') return 'lưu/đọc dữ liệu tạm lỗi';
    if (code === 'BOARD_SHEET_AUTO_SKIPPED') return 'máy đã bỏ qua tấm storyboard bị bộ lọc nội dung chặn';
    if (code === 'LLM_POST_RESPONSE_FAILED') return 'AI đã trả lời nhưng máy chưa xử lý được kết quả';
    return null;
  }).filter((x): x is string => !!x)));
  return text.length ? text.join(', ') : null;
}

/** Suy banner CHỈ từ `chain.state` + `autoRetry` + giờ máy (contract §1) — không từ bộ đếm RAM của BE. */
export function autoRetryViewOf(chain: { state?: string; segment?: number; autoRetry?: WorkflowFourAutoRetry | null } | null | undefined, nowMs: number = Date.now()): AutoRetryView | null {
  const a = chain?.autoRetry;
  if (!chain || !a || !Number.isFinite(a.round)) return null;
  const round = Math.max(1, Math.min(3, Math.trunc(a.round)));
  const reason = autoRetryReasonOf(a.codes);
  const because = reason ? ` Lý do: ${reason}.` : '';
  if (chain.state === 'RUNNING' && a.segment === chain.segment) {
    return { kind: 'running', round, secondsLeft: 0, title: `Đang chạy lại tự động (lần ${round}/3)`, description: `Máy chỉ làm lại phần lỗi, phần đã xong được giữ nguyên.${because}` };
  }
  if (chain.state !== 'FAILED') return null;
  if (a.exhausted === true) {
    return { kind: 'exhausted', round, secondsLeft: 0, title: 'Máy đã tự thử 3 lần nhưng chưa được', description: `Bấm «Chạy tiếp» để thử lại, hoặc xem lỗi cụ thể bên dưới.${because}` };
  }
  const at = Date.parse(a.at);
  if (!Number.isFinite(at) || at <= nowMs - AUTO_RETRY_GRACE_MS) return null;
  const secondsLeft = Math.max(0, Math.ceil((at - nowMs) / 1000));
  return { kind: 'scheduled', round, secondsLeft, title: secondsLeft > 0 ? `Máy đang tự thử lại (lần ${round}/3, sau ${secondsLeft} giây)` : `Máy đang tự thử lại (lần ${round}/3, sắp chạy)`, description: `Không cần bấm gì. Bạn vẫn có thể bấm «Chạy tiếp» để chạy ngay.${because}` };
}
/** Còn phải poll để thấy lần tự thử chạy: đã lên lịch (chưa quá hạn) hoặc đang chạy lại. */
export const autoRetryPollingOf = (chain: Parameters<typeof autoRetryViewOf>[0], nowMs: number = Date.now()): boolean => {
  const v = autoRetryViewOf(chain, nowMs);
  return v?.kind === 'scheduled' || v?.kind === 'running';
};

/* ───────────── đạo cụ dò thiếu ADVISORY + tự gộp ───────────── */
export const isAdvisoryUnbound = (f: Pick<WorkflowFourFinding, 'code' | 'actionType' | 'severity'> | null | undefined): boolean =>
  !!f && (f.actionType === 'UNBOUND_RESOLVE' || f.code === 'UNBOUND_DISCOVERY' || (f as any).findingId?.startsWith?.('unbound:')) && f.severity === 'ADVISORY';
/** Mục dò thiếu loại đạo cụ còn mở nhưng KHÔNG chặn cổng 1. */
export const advisoryUnboundOf = (status: WorkflowFourStatus | null | undefined): WorkflowFourFinding[] =>
  (status?.unbound ?? []).filter((f) => !!f.findingId && f.severity === 'ADVISORY');

export interface AutoMergedItem { findingId: string; fromId: string; toId: string; fromName: string; toName: string; movedCount: number }
/**
 * Đạo cụ trần máy đã tự gộp vào hàng đầy đủ và chưa khôi phục. Nguồn: `status.autoMerged[]` (BE phát sẵn tên — contract §9);
 * vắng thì suy từ `unboundDecisions[fid].autoMerge`. Tên: `autoMerged[].fromName/toName` → `unboundDecisions[fid].fromName/toName` → `step1.props`
 * (bản trần đã gộp KHÔNG còn trong `step1.props` — BE lọc ra) → nhãn chung. Mỗi `findingId` đúng một thẻ.
 */
export function autoMergedOf(status: (Partial<WorkflowFourStatus> & { autoMerged?: Array<Record<string, any>> | null }) | null | undefined): AutoMergedItem[] {
  const decisions = (status?.unboundDecisions ?? {}) as Record<string, any>;
  const propName = (id: string) => String((status?.step1?.props ?? []).find((p: any) => p?.id === id)?.name ?? '').trim();
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : '');
  const rows: Array<Record<string, any>> = Array.isArray(status?.autoMerged)
    ? status!.autoMerged!
    : Object.entries(decisions).flatMap(([findingId, d]) => (d?.action === 'DISMISS' && d?.autoMerge ? [{ findingId, ...d.autoMerge, fromName: d.fromName, toName: d.toName }] : []));
  const seen = new Set<string>();
  const out: AutoMergedItem[] = [];
  for (const r of rows) {
    if (typeof r?.findingId !== 'string' || typeof r.fromId !== 'string' || typeof r.toId !== 'string' || seen.has(r.findingId)) continue;
    seen.add(r.findingId);
    const d = decisions[r.findingId] ?? {};
    out.push({
      findingId: r.findingId, fromId: r.fromId, toId: r.toId,
      fromName: text(r.fromName) || text(d.fromName) || propName(r.fromId) || 'đạo cụ trần',
      toName: text(r.toName) || text(d.toName) || propName(r.toId) || 'đạo cụ đầy đủ',
      movedCount: Array.isArray(r.movedEventIds) ? r.movedEventIds.length : 0,
    });
  }
  return out;
}
/** Câu cho thẻ «Máy đã gộp …». */
export const autoMergedLineOf = (m: AutoMergedItem): string =>
  `Máy đã gộp «${m.fromName}» vào «${m.toName}» (cùng một vật${m.movedCount ? `, ${m.movedCount} video được chuyển sang` : ''}).`;

export const DISMISS_ALL_LABEL = 'Bỏ qua tất cả mục dò thiếu';
export const dismissAllConfirmTextOf = (n: number): string => `Bỏ qua ${n} đạo cụ dò thiếu? Các vật này sẽ không được tạo ảnh ở bước 2 và không vào prompt video. Người / con vật dò thiếu không bị đụng. Quyết định bỏ qua được giữ lại cả khi bước 1 dựng lại; màn này không có nút hoàn tác riêng (mọi đạo cụ đã bỏ qua đều có «Khôi phục» ở tab «Cần xem»).`;

export function dismissAllOutcomeOf(response: any): { message: string; level: 'success' | 'info' } {
  const n = Array.isArray(response?.dismissed) ? response.dismissed.length : 0;
  return n ? { message: `Đã bỏ qua ${n} đạo cụ dò thiếu. Cổng 1 đã đổi — đọc lại rồi duyệt.`, level: 'success' } : { message: 'Không còn đạo cụ dò thiếu nào để bỏ qua.', level: 'info' };
}
export function restoreOutcomeOf(response: any, m?: { name?: string | null; fromName?: string | null; toName?: string | null } | null): { message: string; level: 'success' | 'warning' | 'info' } {
  if (response?.finding?.alreadyRestored === true) return { message: 'Mục này đã được khôi phục trước đó.', level: 'info' };
  const r = response?.restored;
  const returned = Array.isArray(r?.returnedEventIds) ? r.returnedEventIds.length : 0;
  const skipped = Array.isArray(r?.skippedEventIds) ? r.skippedEventIds.length : 0;
  const label = m?.name || m?.fromName;
  const name = label ? `«${label}»` : 'đạo cụ';
  const base = `Đã khôi phục ${name}${returned ? `, trả ${returned} video về lại` : ''}.`;
  return skipped
    ? { message: `${base} ${skipped} video bạn đã tự gắn vào ${m?.toName ? `«${m.toName}»` : 'hàng đầy đủ'} được giữ nguyên. Cổng 1 đã đổi — đọc lại rồi duyệt.`, level: 'warning' }
    : { message: `${base} Cổng 1 đã đổi — đọc lại rồi duyệt.`, level: 'success' };
}
const UNBOUND_ACTION_ERROR: Record<string, string> = {
  STEP1_HASH_MISMATCH: 'Bước ① vừa đổi sau khi bạn xem — đã tải lại, hãy xem rồi bấm lại.',
  STEP1_NOT_READY: 'Bước ① chưa chạy xong.',
  WORKFLOW_RUN_ACTIVE: 'Máy đang chạy — đợi xong hoặc bấm Dừng rồi thử lại.',
  PLAN_BUSY: PLAN_BUSY_TEXT,   // + tải lại (unboundActionReloadsOf) rồi người bấm lại
  NOT_AUTO_MERGED: 'Mục này không phải đạo cụ máy tự gộp (hoặc đã khôi phục rồi) — đã tải lại.',
  NOT_DISMISSED: 'Mục này chưa ở trạng thái Bỏ qua (có thể đã khôi phục rồi) — đã tải lại.',
  BAD_BODY: 'Yêu cầu không hợp lệ — tải lại trang rồi thử lại.',
  NOT_FOUND: 'Không còn mục này — đã tải lại.',
};
/** Câu lỗi cho «Khôi phục» / «Bỏ qua tất cả»; mã lạ ⇒ null (caller dùng apiErrorText). */
export function unboundActionErrorTextOf(err: any): string | null {
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : typeof d?.errorCode === 'string' ? d.errorCode : '';
  return UNBOUND_ACTION_ERROR[code] ?? null;
}
/** Lỗi ở các code này nghĩa là dữ liệu trên màn đã cũ ⇒ tải lại. */
export const unboundActionReloadsOf = (err: any): boolean => ['STEP1_HASH_MISMATCH', 'PLAN_BUSY', 'NOT_AUTO_MERGED', 'NOT_DISMISSED', 'NOT_FOUND'].includes(String(err?.response?.data?.code ?? err?.response?.data?.errorCode ?? ''));

/* ───────────── S3b: «Khôi phục» MỌI đạo cụ đã Bỏ qua + «Có thể trùng với…» ───────────── */
export interface DismissedPropItem {
  findingId: string; propId: string; kind: 'AUTO_MERGE' | 'MANUAL';
  /** Tên đạo cụ đã bỏ qua (bản trần nếu máy gộp). */
  name: string; toName: string | null; movedCount: number;
  /** Lý do người ghi khi bỏ qua tay (không có ở bản máy gộp). */
  reason: string | null;
  /** Cảnh báo nhẹ «cùng loại, cần kiểm tra nguồn» (BE `dismissedPropWarnings`) — chỉ để nhắc, không quyết định có nút hay không. */
  warning: string | null;
}
const PROP_FINDING = /^unbound:PROP:(.+)$/;
/**
 * MỌI đạo cụ đang ở trạng thái «Bỏ qua» (`unboundDecisions[fid].action === 'DISMISS'`, findingId `unbound:PROP:*`) — tay lẫn máy gộp — đều Khôi phục được.
 * Tên: autoMerged[]/decision → dismissedPropWarnings[].name → step1.props theo id → nhãn chung. Bản máy gộp đứng trước.
 */
export function dismissedPropsOf(status: (Partial<WorkflowFourStatus> & { autoMerged?: Array<Record<string, any>> | null; dismissedPropWarnings?: Array<Record<string, any>> | null }) | null | undefined): DismissedPropItem[] {
  const decisions = (status?.unboundDecisions ?? {}) as Record<string, any>;
  const warnings = (Array.isArray(status?.dismissedPropWarnings) ? status!.dismissedPropWarnings! : []).filter((w) => typeof w?.findingId === 'string');
  const warningOf = (fid: string): { name: string; message: string } => {
    const w = warnings.find((x) => x.findingId === fid);
    return { name: typeof w?.name === 'string' ? w.name.trim() : '', message: typeof w?.message === 'string' ? w.message.trim() : '' };
  };
  const out: DismissedPropItem[] = autoMergedOf(status).filter((m) => PROP_FINDING.test(m.findingId)).map((m) => ({
    findingId: m.findingId, propId: m.fromId, kind: 'AUTO_MERGE' as const, name: m.fromName, toName: m.toName, movedCount: m.movedCount,
    reason: typeof decisions[m.findingId]?.reason === 'string' ? decisions[m.findingId].reason : null, warning: warningOf(m.findingId).message || null,
  }));
  const seen = new Set(out.map((m) => m.findingId));
  for (const [findingId, d] of Object.entries(decisions)) {
    const propId = PROP_FINDING.exec(findingId)?.[1];
    if (!propId || seen.has(findingId) || d?.action !== 'DISMISS') continue;
    seen.add(findingId);
    const w = warningOf(findingId);
    const inRoster = String((status?.step1?.props ?? []).find((p: any) => p?.id === propId)?.name ?? '').trim();
    out.push({ findingId, propId, kind: 'MANUAL', name: w.name || inRoster || 'đạo cụ đã bỏ qua', toName: null, movedCount: 0,
      reason: typeof d?.reason === 'string' && d.reason.trim() ? d.reason.trim() : null, warning: w.message || null });
  }
  return out;
}
/** Dòng chính của thẻ «Đã bỏ qua» (bản tay). */
export const dismissedPropLineOf = (m: DismissedPropItem): string =>
  `Đã bỏ qua «${m.name}»${m.reason ? ` — lý do: ${m.reason}` : ''}. Vật này không được tạo ảnh ở bước 2 và không vào prompt video.`;

/** «Khôi phục» gửi lại sau lỗi mạng PHẢI mang đúng `requestId` + `baseHash` của lần đầu (BE replay kết quả cũ, không báo STEP1_HASH_MISMATCH). */
export interface PendingRestore { findingId: string; baseHash: string; requestId: string }
export function restoreRequestOf(pending: PendingRestore | null | undefined, findingId: string, currentHash: string, makeId: () => string): PendingRestore {
  return pending && pending.findingId === findingId ? pending : { findingId, baseHash: currentHash, requestId: makeId() };
}
/** Không biết lần gọi có tới BE không (mất mạng / timeout / cổng 5xx) ⇒ giữ `requestId` cho lần bấm sau. Có phản hồi 2xx/4xx ⇒ xong, bỏ. */
export function restoreOutcomeUncertain(err: any): boolean {
  const status = err?.response?.status;
  return !err?.response || (typeof status === 'number' && status >= 500);
}

export interface PossibleDuplicateItem { key: string; propName: string; candidates: string[]; reason: string; findingId: string | null; /** Có nút xử lý: mục dò thiếu đó còn mở (`status.unbound`). */ canResolve: boolean }
/**
 * `step1.possibleDuplicates` (BE giữ riêng đạo cụ có câu nguồn riêng nhưng có thể cùng vật). Chỉ để hiển thị: rỗng/vắng/sai hình ⇒ []. Không đụng nút duyệt cổng.
 */
export function possibleDuplicatesOf(status: { step1?: { possibleDuplicates?: unknown } | null; unbound?: Array<{ findingId?: string }> | null } | null | undefined): PossibleDuplicateItem[] {
  const raw = status?.step1?.possibleDuplicates;
  if (!Array.isArray(raw)) return [];
  const open = new Set((status?.unbound ?? []).map((f) => f?.findingId).filter((x): x is string => !!x));
  const out: PossibleDuplicateItem[] = [];
  const seen = new Set<string>();
  for (const r of raw as Array<Record<string, any>>) {
    const propName = typeof r?.propName === 'string' ? r.propName.trim() : '';
    const candidates = (Array.isArray(r?.candidates) ? r.candidates : []).filter((c: unknown): c is string => typeof c === 'string' && !!c.trim()).map((c: string) => c.trim());
    const reasonText = typeof r?.reason === 'string' ? r.reason.trim() : '';
    if (!propName || (!candidates.length && !reasonText)) continue;
    const findingId = typeof r?.findingId === 'string' && r.findingId ? r.findingId : null;
    const key = `${propName}|${findingId ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, propName, candidates, findingId, canResolve: !!findingId && open.has(findingId),
      reason: reasonText || `Có thể trùng với ${candidates.map((c) => `«${c}»`).join(', ')} — gộp tay nếu cùng một vật` });
  }
  return out;
}

/* ───────────── S12g fix1 (NOTE-2): «Máy đã viết lại trung tính do bộ lọc nội dung» ───────────── */
export type NeutralizedStep = 'STEP1' | 'ASSETS' | 'BOARD' | 'PROMPT';
const NEUTRAL_STEP_NAME: Record<NeutralizedStep, string> = { STEP1: 'Bước ①', ASSETS: 'Bước ②', BOARD: 'Bước ③', PROMPT: 'Bước ④ (viết prompt)' };
/** Cảnh báo nhỏ cho ĐÚNG bước đang xem; không có (hoặc bước khác) ⇒ null. Không chặn, không nút. */
export function neutralizedNoteOf(items: Array<{ step: NeutralizedStep; count: number; labels?: string[] }> | null | undefined, step: NeutralizedStep | null): { title: string; description: string } | null {
  const it = step ? (items ?? []).find((x) => x.step === step && x.count > 0) : undefined;
  if (!it) return null;
  const which = it.labels?.length ? ` (${it.labels.slice(0, 5).join(', ')}${it.labels.length > 5 ? '…' : ''})` : '';
  return { title: 'Máy đã viết lại trung tính do bộ lọc nội dung', description: `${NEUTRAL_STEP_NAME[step!]}: ${it.count} mục bị nhà cung cấp AI chặn nội dung nên máy tự viết lại bằng giọng trung tính, vẫn giữ nguyên sự kiện nguồn${which}. Đọc lại các mục này; nếu muốn đúng văn phong gốc, hãy sửa tay.` };
}
